/* PanelSignUp.js */
/* @author: anghello */

import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Row } from 'reactstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import WAM from '../../../services/WAM';
import Strings from '../../../translations/Translations';
import PasswordInput from './input/PasswordInput';
import * as Yup from 'yup';
import { useAuth } from '../../../Context/Auth/AuthContext';

const schema = Yup.object().shape({
  username: Yup.string().required(Strings('username_is_required')),
  email: Yup.string().email(Strings('email_invalid')).required(Strings('email_is_required')),
  password: Yup.string().required(Strings('password_is_required')).min(4, Strings('password_must_be_at_least_4_characters'))
});

export default function PanelSignUp() {
  const { toggleLoading } = useAuth();
  const history = useHistory();
  const { register, errors, handleSubmit, reset } = useForm({
    validationSchema: schema,
  });

  const onSubmit = useCallback(async ({ username, email, password }) => {
    toggleLoading(true);

    try {
      const response = await WAM.signup({ un: username, email, pwd: password });

      if (response.result === 'success') {
        toast.success(Strings('user_successfully_created'));
        reset();
        history.replace('/login');
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_create_user');
        toast.error(message);
      }
    } catch (err) {
      toast.error(Strings('general_error'));
    } finally {
      toggleLoading(false);
    }
  }, [toggleLoading, reset, history]);

  return (
    <>
      <div className='mb-2 mb-0'>
        <small className="text-muted">&nbsp;</small>
      </div>

      <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Row>
          <Col md={12}>
            <FormGroup>
              <input
                type='text'
                name='username'
                placeholder={Strings('username')}
                ref={register}
                className='form-control pb-input-1 border-radius-4px'
              />
              {errors.username && (
                <p role='alert' className='text-danger txt-small-1'>
                  {errors.username.message}
                </p>
              )}
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <input
                type='email'
                name='email'
                placeholder={Strings('email')}
                ref={register}
                className='form-control pb-input-1 border-radius-4px'
              />
              {errors.email && (
                <p role='alert' className='text-danger txt-small-1'>
                  {errors.email.message}
                </p>
              )}
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <PasswordInput
                name='password'
                placeholder={Strings('password')}
                ref={register}
                className='form-control pb-input-1 border-radius-4px'
              />
              {errors.password && (
                <p role='alert' className='text-danger txt-small-1'>
                  {errors.password.message}
                </p>
              )}
            </FormGroup>
          </Col>
          <Col md={12} className='mb-4'>
            <div className="text-center p">
              <small>
                {Strings('by_signing_up_you_agree_with_our')} <a href="https://www.photozig.com/privacy_terms.html" target="_blank" rel="noreferrer noopener">{Strings('terms_and_conditions')}</a> / <a href="https://www.photozig.com/privacy_terms.html" target="_blank" rel="noreferrer noopener">{Strings('privacy_policy')}</a>.
              </small>
            </div>
          </Col>
        </Row>

        <div className='d-flex justify-content-center'>
          <Button
            color='primary'
            size='lg'
            type='submit'
            onClick={handleSubmit(onSubmit)}
          >
            {Strings('create_account')}
          </Button>
        </div>
      </Form>
    </>
  );
}
